import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';
import { useSiteConfig } from '../contexts/SiteConfigProvider';
import { useArtList } from '../contexts/ArtListProvider';

const AdminPanel: React.FC = () => {
    const { activeTone, setActiveTone } = useSiteConfig();
    const { artList } = useArtList();

    const [adminKey, setAdminKey] = useState('');
    const [unlocked, setUnlocked] = useState(false);
    const [selectedTone, setSelectedTone] = useState(activeTone);
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const availableTones = useMemo(() => {
        const toneSet = new Set<string>();
        artList.forEach((art) => {
            if (art.descriptions) {
                Object.keys(art.descriptions).forEach((t) => toneSet.add(t));
            }
        });
        if (toneSet.size === 0) toneSet.add('standard');
        return Array.from(toneSet).sort();
    }, [artList]);

    if (new URLSearchParams(window.location.search).get('admin') !== '1') return null;

    const handleUnlock = async () => {
        setStatus(null);
        try {
            const res = await fetch('/admin/api/tone', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tone: activeTone, adminKey }),
            });
            if (res.status === 401) {
                setStatus({ type: 'error', message: 'Incorrect admin key.' });
                return;
            }
            setUnlocked(true);
            setSelectedTone(activeTone);
        } catch {
            setStatus({ type: 'error', message: 'Could not reach admin service.' });
        }
    };

    const handleApply = async () => {
        setStatus(null);
        try {
            const res = await fetch('/admin/api/tone', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tone: selectedTone, adminKey }),
            });
            if (res.status === 401) {
                setStatus({ type: 'error', message: 'Incorrect admin key.' });
                setUnlocked(false);
                return;
            }
            setActiveTone(selectedTone);
            setStatus({ type: 'success', message: `Active tone set to "${selectedTone}".` });
        } catch {
            setStatus({ type: 'error', message: 'Could not reach admin service.' });
        }
    };

    return (
        <Box
            data-testid="admin-panel"
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 2000,
                backgroundColor: '#0f1929',
                border: '1px solid #c9a84c',
                p: 3,
                width: 280,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}
        >
            <Typography variant="caption" sx={{ color: '#c9a84c', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Admin — Tone Control
            </Typography>

            {!unlocked ? (
                <>
                    <TextField
                        slotProps={{ htmlInput: { 'data-testid': 'admin-key-input' } }}
                        type="password"
                        placeholder="Admin key"
                        size="small"
                        value={adminKey}
                        onChange={(e) => setAdminKey(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                        sx={{ input: { color: '#f0ede8', fontSize: '0.85rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' } }}
                    />
                    <Button
                        size="small"
                        onClick={handleUnlock}
                        sx={{ color: '#c9a84c', borderColor: '#c9a84c', fontSize: '0.75rem' }}
                        variant="outlined"
                    >
                        Unlock
                    </Button>
                </>
            ) : (
                <>
                    <Select
                        data-testid="tone-select"
                        size="small"
                        value={selectedTone}
                        onChange={(e) => setSelectedTone(e.target.value)}
                        MenuProps={{ sx: { zIndex: 3000 } }}
                        sx={{ color: '#f0ede8', fontSize: '0.85rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' } }}
                    >
                        {availableTones.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        data-testid="tone-confirm"
                        size="small"
                        onClick={handleApply}
                        sx={{ color: '#c9a84c', borderColor: '#c9a84c', fontSize: '0.75rem' }}
                        variant="outlined"
                    >
                        Apply tone
                    </Button>
                </>
            )}

            {status && (
                <Typography
                    variant="caption"
                    sx={{ color: status.type === 'success' ? '#7ecb8f' : '#e57373', fontSize: '0.75rem' }}
                >
                    {status.message}
                </Typography>
            )}
        </Box>
    );
};

export default AdminPanel;
